// AYA Transport — hub reproduisant strictement la maquette de référence (dense, mobile-first).
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, Ellipsis, CarTaxiFront, Bus, Plane, UserRound, ArrowRight, ShieldCheck,
  Bike, Truck, BusFront, KeyRound, CarFront, Home, Search, Store, MessageSquare, User, ShieldPlus, Headset, HandCoins,
} from "lucide-react";
import FlagCi from "@/components/FlagCi";
import AyaMenu from "@/components/AyaMenu";

const IMG = "/aya/transport";
const ctx = (m) => `/aya-prive?ctx=${encodeURIComponent(m)}`;

const MAIN_CARDS = [
  { id: "taxi", Icon: CarTaxiFront, color: "#34D399", border: "rgba(52,211,153,0.65)", img: `${IMG}/tp_taxi.jpg`, title: "Réserver un taxi", desc: "Proposez votre prix et trouvez un chauffeur partenaire à proximité.", route: "/transport/taxi" },
  { id: "car", Icon: Bus, color: "#60A5FA", border: "rgba(96,165,250,0.6)", img: `${IMG}/tp_car.jpg`, title: "Acheter un billet de car", desc: "Voyagez vers toutes les grandes villes avec nos compagnies partenaires.", route: "/transport/car" },
  { id: "avion", Icon: Plane, color: "#A78BFA", border: "rgba(167,139,250,0.6)", img: `${IMG}/tp_avion.jpg`, title: "Acheter un billet d'avion", desc: "Voyagez en Côte d'Ivoire et à l'international.", route: "/transport/avion" },
  { id: "chauffeur", Icon: UserRound, color: "#FFFFFF", border: "rgba(236,245,238,0.35)", img: `${IMG}/tp_chauffeur.jpg`, title: "Chauffeur privé", desc: "Un chauffeur à votre service pour tous vos trajets.", route: "/transport/chauffeur" },
];

const OTHER_SERVICES = [
  { id: "motos", Icon: Bike, color: "#34D399", label: "Motos", active: true, msg: "Je veux réserver une moto avec AYA Transport." },
  { id: "tricycle", Icon: Truck, color: "#F5A623", label: "Tricycle", msg: "Je veux réserver un tricycle avec AYA Transport." },
  { id: "eleves", Icon: BusFront, color: "#F5A623", label: "Transport élèves", msg: "Je veux organiser le transport d'élèves avec AYA Transport." },
  { id: "location", Icon: CarFront, color: "#93A8C5", label: "Location de véhicules", msg: "Je veux louer un véhicule avec AYA Location." },
  { id: "louer-moto", Icon: KeyRound, color: "#93A8C5", label: "Louer une moto", msg: "Je veux louer une moto avec AYA Location." },
];

const GUARANTEES = [
  { Icon: ShieldPlus, label: "Couverture en cas d'accident" },
  { Icon: Headset, label: "Assistance pendant le trajet" },
  { Icon: HandCoins, label: "Indemnisation selon garanties" },
];

const DESTINATIONS = [
  { id: "bouake", img: `${IMG}/tp_dest_bouake.jpg`, name: "Bouaké", price: "6 000 FCFA" },
  { id: "yamoussoukro", img: `${IMG}/tp_dest_yamoussoukro.jpg`, name: "Yamoussoukro", price: "7 500 FCFA" },
  { id: "sanpedro", img: `${IMG}/tp_dest_sanpedro.jpg`, name: "San-Pédro", price: "9 000 FCFA" },
  { id: "korhogo", img: `${IMG}/tp_dest_korhogo.jpg`, name: "Korhogo", price: "7 000 FCFA" },
  { id: "man", img: `${IMG}/tp_dest_man.jpg`, name: "Man", price: "8 000 FCFA" },
];

export default function TransportHubPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const NAV = [
    { id: "accueil", Icon: Home, label: "Accueil", go: () => navigate("/search") },
    { id: "rechercher", Icon: Search, label: "Rechercher", go: () => navigate("/search") },
    { id: "commerce", Icon: Store, label: "Commerce", go: () => navigate("/commerce") },
    { id: "transport", Icon: BusFront, label: "Transport", active: true, go: () => window.scrollTo({ top: 0, behavior: "smooth" }) },
    { id: "messages", Icon: MessageSquare, label: "Messages", badge: 3, go: () => navigate("/aya-prive") },
    { id: "profil", Icon: User, label: "Profil", go: () => navigate("/mon-compte") },
  ];

  return (
    <div style={S.page} data-testid="transport-hub-page">
      <AyaMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* ── HERO ── */}
      <div style={S.hero} data-testid="transport-hero">
        <img src={`${IMG}/tp_hub_hero.jpg`} alt="AYA Transport" style={S.heroImg} />
        <div style={S.heroOverlay} />
        <div style={S.heroTop}>
          <div style={{ flex: 1 }} />
          <button style={S.heroIconBtn} aria-label="Notifications" data-testid="transport-bell-btn" onClick={() => setMenuOpen(true)}>
            <Bell size={19} strokeWidth={2.2} />
            <span style={S.bellDot} />
          </button>
          <button style={S.heroIconBtn} aria-label="Plus d'options" data-testid="transport-more-btn" onClick={() => setMenuOpen(true)}>
            <Ellipsis size={20} strokeWidth={2.4} />
          </button>
        </div>
        <div style={S.heroContent}>
          <div style={S.heroTitle}>AYA <span style={{ color: "#2FCF62" }}>Transport</span> <FlagCi size={20} style={{ verticalAlign: "-1px" }} /></div>
          <p style={S.heroSlogan}>Déplacez-vous simplement<br />en toute sécurité.</p>
        </div>
      </div>

      <div style={S.container}>
        {/* ── 4 CARTES PRINCIPALES ── */}
        <div style={S.mainGrid} data-testid="transport-main-cards">
          {MAIN_CARDS.map((c) => (
            <button key={c.id} style={{ ...S.mainCard, borderColor: c.border }} data-testid={`transport-main-${c.id}`} onClick={() => navigate(c.route)}>
              <span style={S.mainHead}>
                <c.Icon size={22} color={c.color} strokeWidth={2.2} />
                <span style={S.arrowCircle}><ArrowRight size={14} color="#0B3B27" strokeWidth={2.6} /></span>
              </span>
              <span style={S.mainTitle}>{c.title}</span>
              <span style={S.mainDesc}>{c.desc}</span>
              <img src={c.img} alt={c.title} style={S.mainImg} loading="lazy" />
            </button>
          ))}
        </div>

        {/* ── AUTRES SERVICES ── */}
        <div style={S.secHead}>
          <h2 style={S.secTitle}>Autres services</h2>
          <button style={S.secMore} data-testid="transport-others-see-all" onClick={() => navigate(ctx("Je veux voir tous les services AYA Transport : motos, tricycles, transport élèves, location de véhicules."))}>Voir tout <ArrowRight size={13} /></button>
        </div>
        <div style={S.serviceRow} data-testid="transport-other-services">
          {OTHER_SERVICES.map((s) => (
            <button key={s.id} data-testid={`transport-service-${s.id}`} onClick={() => navigate(ctx(s.msg))}
              style={{ ...S.serviceCard, ...(s.active ? S.serviceActive : {}) }}>
              <s.Icon size={22} color={s.color} strokeWidth={2.1} />
              <span style={S.serviceLabel}>{s.label}</span>
            </button>
          ))}
        </div>

        {/* ── BANNIÈRE PROTECTION ── */}
        <div style={S.protect} data-testid="transport-protection-banner">
          <img src={`${IMG}/tp_protection.jpg`} alt="" style={S.protectImg} loading="lazy" />
          <div style={S.protectOverlay} />
          <div style={S.protectContent}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={S.shieldCircle}><ShieldCheck size={16} color="#04120B" strokeWidth={2.4} /></span>
              <h3 style={S.protectTitle}>Voyagez en toute sérénité</h3>
            </div>
            <p style={S.protectDesc}>Ajoutez une protection voyage à votre billet de car et soyez couvert en cas d'accident.</p>
            <button style={S.protectBtn} data-testid="transport-protection-btn" onClick={() => navigate(ctx("Je veux en savoir plus sur la protection voyage AYA pour mon billet de car."))}>
              En savoir plus <ArrowRight size={13} />
            </button>
          </div>
          <div style={S.protectPills}>
            {GUARANTEES.map((g) => (
              <span key={g.label} style={S.protectPill}><g.Icon size={11} color="#34D399" /> {g.label}</span>
            ))}
          </div>
        </div>

        {/* ── DESTINATIONS POPULAIRES ── */}
        <div style={S.secHead}>
          <h2 style={S.secTitle}>Destinations populaires</h2>
          <button style={S.secMore} data-testid="transport-dest-see-all" onClick={() => navigate("/transport/car")}>Voir toutes les destinations <ArrowRight size={13} /></button>
        </div>
        <div style={S.destRow} data-testid="transport-destinations">
          {DESTINATIONS.map((d) => (
            <button key={d.id} style={S.destCard} data-testid={`transport-dest-${d.id}`} onClick={() => navigate("/transport/car")}>
              <img src={d.img} alt={d.name} style={S.destImg} loading="lazy" />
              <span style={S.destName}>{d.name}</span>
              <span style={S.destFrom}>À partir de</span>
              <span style={S.destPrice}>{d.price}</span>
            </button>
          ))}
        </div>
        {/* ── DEVENEZ CHAUFFEUR PARTENAIRE ── */}
        <button style={S.driverCta} data-testid="transport-driver-cta" onClick={() => navigate("/chauffeur")}>
          <img src="/aya/chauffeur/chf_driver.jpg" alt="" style={S.driverImg} loading="lazy" />
          <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
            <span style={{ display: "block", fontSize: 13.5, fontWeight: 800, color: "#FFFFFF" }}>Devenez chauffeur partenaire</span>
            <span style={{ fontSize: 10, color: "#BBE7CF", fontWeight: 600, lineHeight: 1.3 }}>Plus de courses, plus de revenus en toute sécurité. Rejoignez AYA Chauffeur.</span>
          </span>
          <span style={S.arrowCircle}><ArrowRight size={14} color="#0B3B27" strokeWidth={2.6} /></span>
        </button>
      </div>

      {/* ── NAVIGATION BASSE ── */}
      <nav style={S.bottomNav} data-testid="transport-bottom-nav">
        {NAV.map((t) => (
          <button key={t.id} data-testid={`transport-nav-${t.id}`} onClick={t.go} aria-label={t.label}
            style={{ ...S.navItem, ...(t.active ? S.navActive : {}) }}>
            <span style={{ position: "relative", display: "flex" }}>
              <t.Icon size={19} strokeWidth={t.active ? 2.4 : 2} />
              {t.badge && <span style={S.navBadge}>{t.badge}</span>}
            </span>
            <span style={{ fontSize: 9.5, fontWeight: t.active ? 800 : 600 }}>{t.label}</span>
            {t.active && <span style={S.navUnderline} />}
          </button>
        ))}
      </nav>
    </div>
  );
}

const S = {
  page: { minHeight: "100vh", background: "radial-gradient(ellipse at top, #072115 0%, #04120B 60%, #030B07 100%)", color: "#ECF5EE", paddingBottom: 86, overflowX: "hidden" },
  container: { maxWidth: 880, margin: "0 auto", padding: "0 12px", minWidth: 0 },
  hero: { position: "relative", height: 208, overflow: "hidden" },
  heroImg: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 30%" },
  heroOverlay: { position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(3,15,9,0.45), rgba(3,15,9,0.15) 40%, rgba(4,18,11,0.96) 96%)" },
  heroTop: { position: "relative", display: "flex", alignItems: "center", gap: 2, padding: "8px 8px 0", maxWidth: 880, margin: "0 auto" },
  heroIconBtn: { position: "relative", background: "none", border: "none", color: "#FFFFFF", cursor: "pointer", padding: 6, display: "flex" },
  bellDot: { position: "absolute", top: 5, right: 6, width: 7, height: 7, borderRadius: "50%", background: "#EF4444", border: "1.5px solid rgba(4,18,11,0.8)" },
  heroContent: { position: "relative", maxWidth: 880, margin: "0 auto", padding: "8px 14px" },
  heroTitle: { fontSize: 27, fontWeight: 900, color: "#FFFFFF", letterSpacing: 0.4, lineHeight: 1 },
  heroSlogan: { margin: "7px 0 0", fontSize: 12.5, fontWeight: 600, color: "#FFFFFF", lineHeight: 1.35, textShadow: "0 1px 6px rgba(0,0,0,0.5)" },
  mainGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 9, marginTop: 10 },
  mainCard: { display: "flex", flexDirection: "column", gap: 4, padding: "11px 11px 10px", borderRadius: 17, background: "linear-gradient(160deg, rgba(8,38,24,0.85), rgba(4,20,13,0.9))", border: "1.5px solid", cursor: "pointer", color: "#ECF5EE", textAlign: "left" },
  mainHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  arrowCircle: { width: 26, height: 26, borderRadius: "50%", background: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  mainTitle: { fontSize: 14.5, fontWeight: 800, color: "#FFFFFF", lineHeight: 1.15, marginTop: 3 },
  mainDesc: { fontSize: 9.8, color: "#CFE5D6", lineHeight: 1.35, flex: 1 },
  mainImg: { width: "100%", height: 82, objectFit: "cover", borderRadius: 12, marginTop: 6 },
  secHead: { display: "flex", justifyContent: "space-between", alignItems: "center", margin: "13px 0 8px", gap: 8 },
  secTitle: { fontSize: 15.5, fontWeight: 800, margin: 0, color: "#FFFFFF" },
  secMore: { display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", color: "#34D399", fontSize: 11.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 },
  serviceRow: { display: "flex", gap: 8, overflowX: "auto", scrollbarWidth: "none", paddingBottom: 3 },
  serviceCard: { flex: "0 0 auto", minWidth: 96, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, height: 76, padding: "0 10px", borderRadius: 14, background: "rgba(8,38,24,0.6)", border: "1px solid rgba(16,185,129,0.25)", cursor: "pointer", color: "#ECF5EE" },
  serviceActive: { background: "rgba(16,185,129,0.16)", border: "1.5px solid rgba(52,211,153,0.65)" },
  serviceLabel: { fontSize: 10, fontWeight: 700, lineHeight: 1.15, textAlign: "center", maxWidth: 92 },
  protect: { position: "relative", display: "flex", alignItems: "center", gap: 8, marginTop: 12, padding: 12, borderRadius: 17, overflow: "hidden", border: "1px solid rgba(16,185,129,0.35)", minHeight: 118 },
  protectImg: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "62% center" },
  protectOverlay: { position: "absolute", inset: 0, background: "linear-gradient(92deg, rgba(4,24,15,0.96) 34%, rgba(4,24,15,0.72) 58%, rgba(4,24,15,0.28))" },
  protectContent: { position: "relative", flex: 1, minWidth: 0 },
  shieldCircle: { width: 27, height: 27, borderRadius: "50%", background: "#34D399", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  protectTitle: { margin: 0, fontSize: 13.5, fontWeight: 800, color: "#FFFFFF" },
  protectDesc: { margin: "6px 0 8px", fontSize: 9.8, color: "#DFF0E4", lineHeight: 1.4, maxWidth: 250 },
  protectBtn: { display: "inline-flex", alignItems: "center", gap: 5, height: 29, padding: "0 12px", borderRadius: 999, border: "none", background: "#FFFFFF", color: "#0B3B27", fontSize: 10, fontWeight: 800, cursor: "pointer" },
  protectPills: { position: "relative", display: "flex", flexDirection: "column", gap: 5, flexShrink: 0, maxWidth: 150 },
  protectPill: { display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 8px", borderRadius: 9, background: "rgba(4,24,15,0.78)", border: "1px solid rgba(52,211,153,0.45)", color: "#E8F6EC", fontSize: 8.3, fontWeight: 700, lineHeight: 1.1, backdropFilter: "blur(6px)" },
  destRow: { display: "flex", gap: 9, overflowX: "auto", scrollbarWidth: "none", paddingBottom: 3 },
  destCard: { flex: "0 0 31%", minWidth: 120, maxWidth: 152, display: "flex", flexDirection: "column", gap: 2, padding: 6, borderRadius: 13, background: "rgba(8,38,24,0.65)", border: "1px solid rgba(16,185,129,0.28)", cursor: "pointer", color: "#ECF5EE", textAlign: "left" },
  destImg: { width: "100%", height: 78, objectFit: "cover", borderRadius: 10 },
  destName: { fontSize: 12, fontWeight: 800, marginTop: 4, padding: "0 2px" },
  destFrom: { fontSize: 8.8, color: "#9DB4A6", padding: "0 2px" },
  destPrice: { fontSize: 12, fontWeight: 800, color: "#34D399", padding: "0 2px 2px" },
  driverCta: { display: "flex", alignItems: "center", gap: 10, width: "100%", marginTop: 13, padding: 12, borderRadius: 17, background: "linear-gradient(140deg, #0B3B27, #06281A)", border: "1.5px solid rgba(52,211,153,0.55)", cursor: "pointer" },
  driverImg: { width: 46, height: 46, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(52,211,153,0.7)", flexShrink: 0 },
  bottomNav: { position: "fixed", bottom: 8, left: 10, right: 10, zIndex: 60, display: "flex", justifyContent: "space-around", padding: "7px 4px calc(7px + env(safe-area-inset-bottom))", background: "#FFFFFF", borderRadius: 22, boxShadow: "0 8px 28px rgba(0,0,0,0.45)", maxWidth: 860, margin: "0 auto" },
  navItem: { position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, background: "none", border: "none", color: "#4B5563", cursor: "pointer", padding: "3px 7px" },
  navActive: { color: "#0E7A4F" },
  navUnderline: { position: "absolute", bottom: -3, left: "50%", transform: "translateX(-50%)", width: 30, height: 3, borderRadius: 3, background: "#0E7A4F" },
  navBadge: { position: "absolute", top: -5, right: -8, minWidth: 15, height: 15, borderRadius: 999, background: "#EF4444", color: "#fff", fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px", border: "1.5px solid #FFFFFF" },
};
